import { formatDistanceToNow } from 'date-fns'
import { enUS, vi } from 'date-fns/locale'
import { useRouter } from 'next/router'
import * as React from 'react'

import { applyNotificationNavigation } from '@/lib/apply-notification-navigation'
import { resolveNotificationNavigationTarget } from '@/lib/notification-navigation'
import useLocale from '@/hooks/use-locale'
import useTabVisibility from '@/hooks/use-tab-visibility'

import { WebSocketMessageAction } from '@/config/websocket.config'
import { WEBSOCKET_EVENTS } from '@/constant/websocket-events'
import { useAuth } from '@/contexts/auth-context'
import { useChat } from '@/contexts/chat-context'
import notificationService from '@/services/notification.service'
import tabNotificationService from '@/services/tab-notification.service'
import { normalizeNotificationSocketPayload } from '@/utils/notification-socket-payload'

import { useSocket } from './socket-context'

import { UserNotificationType } from '@/types/notification.type'

interface NotificationContextValue {
  notifications: UserNotificationType[]
  unreadCount: number
  refresh: () => void
  resetUnreadCount: () => void
  openNotificationDetail: (notification: UserNotificationType) => Promise<void>
  navigateToNotifications: () => void
  formattedTimeAgo: (dateString: string) => string
  tabNotificationCount: number
  clearTabNotifications: () => void
}

const NotificationContext = React.createContext<NotificationContextValue | undefined>(undefined)

export const useNotification = (): NotificationContextValue => {
  const context = React.useContext(NotificationContext)
  if (!context) {
    throw new Error('useNotification must be used within a NotificationProvider')
  }
  return context
}

// Utility function to parse destination URL for chat notifications
export const parseChatDestinationUrl = (destinationUrl: string) => {
  try {
    const baseUrl = typeof window !== 'undefined' ? window.location.origin : 'http://localhost'
    const url = new URL(destinationUrl, baseUrl)
    const conversationId = url.searchParams.get('conversationId')
    const messageId = url.searchParams.get('messageId')
    const normalizedPath = url.pathname.replace(/\/+$/, '').toLowerCase()

    return {
      conversationId: conversationId ? parseInt(conversationId, 10) : null,
      messageId: messageId ? parseInt(messageId, 10) : null,
      isChatNotification: normalizedPath === '/chat',
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Failed to parse destination URL:', error)
    return {
      conversationId: null,
      messageId: null,
      isChatNotification: false,
    }
  }
}

export const NotificationProvider = ({ children }: { children: React.ReactNode }) => {
  const { user } = useAuth()
  const { lang } = useLocale()
  const { openChat } = useChat()
  const [notifications, setNotifications] = React.useState<UserNotificationType[]>([])
  const [unreadCount, setUnreadCount] = React.useState(0)
  const [refreshFlag, setRefreshFlag] = React.useState(true)
  const [tabNotificationCount, setTabNotificationCount] = React.useState(0)
  const { subscribe, unsubscribe } = useSocket()
  const lastFetchAtRef = React.useRef(0)
  const forceRefreshRef = React.useRef(false)

  const router = useRouter()

  useTabVisibility({
    onVisibilityChange: state => {
      tabNotificationService.setTabVisibility(state.isVisible)
    },
  })

  const formattedTimeAgo = React.useCallback(
    (dateString: string) => {
      const locale = lang === 'vi' ? vi : enUS
      return formatDistanceToNow(new Date(dateString), { addSuffix: true, locale })
    },
    [lang]
  )

  const fetchNotifications = React.useCallback(async () => {
    const now = Date.now()
    const isForced = forceRefreshRef.current
    forceRefreshRef.current = false

    // Skip rapid repeat fetches unless explicitly requested (e.g. mark-as-read).
    if (!isForced && now - lastFetchAtRef.current < 30_000) {
      setRefreshFlag(false)
      return
    }

    try {
      const response = await notificationService.getUserNotification({ pageNumber: 1, pageSize: 5 })
      if (response.data.length > 0) {
        const countUnreadNotifications = response.meta.optional?.unreadNotifications as number
        setNotifications(prev => {
          const fetchedIds = new Set(response.data.map(item => item.id))
          const liveOnly = prev.filter(item => !fetchedIds.has(item.id))
          return [...liveOnly, ...response.data].slice(0, 5)
        })
        setUnreadCount(typeof countUnreadNotifications === 'number' ? countUnreadNotifications : 0)
      } else {
        setNotifications([])
        setUnreadCount(0)
        setTabNotificationCount(0)
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to fetch notifications', error)
    } finally {
      lastFetchAtRef.current = Date.now()
      setRefreshFlag(false)
    }
  }, [])

  const handleIncoming = React.useCallback((data: unknown) => {
    const notification = normalizeNotificationSocketPayload(data)
    if (!notification) {
      return
    }

    setNotifications(prev => {
      if (prev.some(item => item.id === notification.id)) {
        return prev
      }
      return [notification, ...prev].slice(0, 5)
    })
    setUnreadCount(prev => prev + 1)

    tabNotificationService.addNotification({
      title: 'New Notification',
      message: notification.subject,
      source: 'websocket',
      priority: 'medium',
    })
    setTabNotificationCount(prev => prev + 1)
  }, [])

  const clearTabNotifications = React.useCallback(() => {
    tabNotificationService.clearNotificationsOnUserInteraction()
    setTabNotificationCount(0)
  }, [])

  React.useEffect(() => {
    if (refreshFlag && user?.id) {
      fetchNotifications()
    }
  }, [fetchNotifications, refreshFlag, user?.id])

  React.useEffect(() => {
    if (!user?.id) return

    const notificationEvents = [
      WebSocketMessageAction.NOTIFICATION,
      WEBSOCKET_EVENTS.NEW_COMMENT,
      WEBSOCKET_EVENTS.NEW_MEME_POST_REACTION,
      WEBSOCKET_EVENTS.NEW_MEME_COMMENT,
      WEBSOCKET_EVENTS.NEW_COMMENT_REACTION,
      WEBSOCKET_EVENTS.MESSAGE_REACTION,
      WEBSOCKET_EVENTS.CHAT_MENTION,
      WEBSOCKET_EVENTS.NEW_POST_MENTION,
      WEBSOCKET_EVENTS.NEW_MEME_COMMENT_REPLY,
      WEBSOCKET_EVENTS.NEW_CODE_REVIEW,
      WEBSOCKET_EVENTS.NEW_ONLINE_TEST_ASSIGNMENT,
    ] as const

    notificationEvents.forEach(eventName => subscribe(eventName, handleIncoming))

    return () => {
      notificationEvents.forEach(eventName => unsubscribe(eventName, handleIncoming))
    }
  }, [user?.id, subscribe, unsubscribe, handleIncoming])

  const openNotificationDetail = React.useCallback(
    async (notification: UserNotificationType) => {
      const target = resolveNotificationNavigationTarget(notification)
      const markSingle = target.kind === 'detail'

      try {
        if (markSingle) {
          if (!notification.isRead) {
            setNotifications(prev =>
              prev.map(n => (n.id === notification.id ? { ...n, isRead: true } : n))
            )
            setUnreadCount(prev => Math.max(0, prev - 1))
            await notificationService.updateUserNotification(notification.id)
            forceRefreshRef.current = true
            setRefreshFlag(true)
          }
        } else {
          const notificationsWithSameUrl = notifications.filter(
            n => n.destinationUrl === notification.destinationUrl
          )
          const unreadNotificationsWithSameUrl = notificationsWithSameUrl.filter(n => !n.isRead)

          setNotifications(prev =>
            prev.map(n => (n.destinationUrl === notification.destinationUrl ? { ...n, isRead: true } : n))
          )

          if (unreadNotificationsWithSameUrl.length > 0) {
            setUnreadCount(prev => Math.max(0, prev - unreadNotificationsWithSameUrl.length))
          }

          await Promise.all(unreadNotificationsWithSameUrl.map(n => notificationService.updateUserNotification(n.id)))

          if (unreadNotificationsWithSameUrl.length > 0) {
            forceRefreshRef.current = true
            setRefreshFlag(true)
          }
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to update notification read status', error)
      } finally {
        applyNotificationNavigation(target, { push: router.push, openChat })
      }
    },
    [openChat, notifications, router]
  )

  const navigateToNotifications = React.useCallback(() => {
    router.push('/notifications')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const refresh = React.useCallback(() => {
    forceRefreshRef.current = true
    setRefreshFlag(true)
  }, [])

  const resetUnreadCount = React.useCallback(() => {
    setUnreadCount(0)
    setNotifications(prev => prev.map(n => ({ ...n, isRead: true })))
  }, [])

  const value = React.useMemo(
    () => ({
      notifications,
      unreadCount,
      refresh,
      resetUnreadCount,
      openNotificationDetail,
      navigateToNotifications,
      formattedTimeAgo,
      tabNotificationCount,
      clearTabNotifications,
    }),
    [
      notifications,
      unreadCount,
      refresh,
      resetUnreadCount,
      openNotificationDetail,
      navigateToNotifications,
      formattedTimeAgo,
      tabNotificationCount,
      clearTabNotifications,
    ]
  )

  return <NotificationContext.Provider value={value}>{children}</NotificationContext.Provider>
}
